import { Box, Typography, Grid, Button, Stack } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useTranslation } from 'react-i18next';
import Reveal from '../components/Reveal';
import { tokens } from '../theme';

const LocationSection = () => {
  const { t } = useTranslation();

  return (
    <Box
      id="location-section"
      sx={{
        py: { xs: 8, md: 14 },
        px: { xs: 2, md: 5 },
      }}
    >
      <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
        {/*
          Was centre-aligned across the whole column. Centred text is hard work
          for a multi-line address and a schedule — every line starts in a
          different place. Left-aligned, the eye has one edge to return to.
        */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Reveal>
            <Typography variant="h2" component="h2" sx={{ textWrap: 'balance' }}>
              {(t as any)('location.title')}
            </Typography>
          </Reveal>

          <Reveal delay={60}>
            <Stack direction="row" spacing={1.5} sx={{ mt: 4 }}>
              <LocationOnIcon sx={{ color: tokens.color.roseInk, mt: '2px' }} aria-hidden="true" />
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
                {(t as any)('location.addressLine1')}
                <br />
                {(t as any)('location.addressLine2')}
              </Typography>
            </Stack>
          </Reveal>

          <Reveal delay={110}>
            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <ScheduleIcon sx={{ color: tokens.color.roseInk, mt: '2px' }} aria-hidden="true" />
              <Box>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600 }}
                  dangerouslySetInnerHTML={{ __html: (t as any)('location.schedule') }}
                />
                {/*
                  Hours are the single most-checked fact on a café site. They get
                  their own tinted panel so they can be found without reading.
                */}
                <Box
                  sx={{
                    mt: 1.5,
                    px: 2.5,
                    py: 2,
                    borderRadius: `${tokens.radius.md}px`,
                    backgroundColor: tokens.color.roseWash,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ color: tokens.color.roseDeep, '& strong': { fontWeight: 700 } }}
                    dangerouslySetInnerHTML={{ __html: (t as any)('location.schedule1') }}
                  />
                  <Typography
                    variant="body1"
                    sx={{ mt: 0.75, color: tokens.color.roseDeep, '& strong': { fontWeight: 700 } }}
                    dangerouslySetInnerHTML={{ __html: (t as any)('location.schedule2') }}
                  />
                </Box>
              </Box>
            </Stack>
          </Reveal>

          <Reveal delay={160}>
            {/*
              The address and schedule rows are indented by their icons, so a
              left-aligned closing line started at the column edge and read as
              misaligned rather than deliberate. Centring both turns the invite
              and its CTA into a closing gesture instead of a ragged third
              alignment.
            */}
            <Box sx={{ mt: 5, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                {(t as any)('location.extraInfo')}
              </Typography>

              <Button
                variant="contained"
                size="large"
                href="https://maps.app.goo.gl/jqDpMApMGB4RS6k96"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<LocationOnIcon />}
                sx={{ mt: 2.5, minWidth: { xs: '100%', sm: 300 } }}
              >
                {(t as any)('location.openInMaps')}
              </Button>
            </Box>
          </Reveal>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Reveal delay={100} distance={20}>
            <Box
              sx={{
                width: '100%',
                height: { xs: 320, md: 540 },
                borderRadius: `${tokens.radius.lg}px`,
                overflow: 'hidden',
                boxShadow: tokens.shadow.md,
              }}
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d233.35784368023295!2d-103.4095461873417!3d20.640042752243712!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8428ad9daf18cf59%3A0x70eefb1d09b6cd60!2sTokki%20Creperie!5e0!3m2!1ses-419!2smx!4v1762217931700!5m2!1ses-419!2smx"
                width="100%"
                height="100%"
                style={{ border: 0, display: 'block' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Tokki Creperie"
              />
            </Box>
          </Reveal>
        </Grid>
      </Grid>
    </Box>
  );
}

export default LocationSection;
